// components/AddressInput.jsx - Service Address input with Google Places typeahead
import React, { useState, useEffect, useRef } from 'react'
import { MapPin, Loader2 } from 'lucide-react'
import * as api from '../services/api'

/**
 * Free-text service-address input backed by Google Places autocomplete (for
 * addresses not on file in Jobber). Selecting a suggestion resolves the full
 * formatted address (incl. postal code) via Place Details.
 * Degrades to a plain text input when Places isn't configured or returns nothing.
 */
export default function AddressInput({ value, onChange, tenantId, placeholder }) {
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [focused, setFocused] = useState(false)

  const requestIdRef = useRef(0)      // guards against stale responses
  const skipSearchRef = useRef(false) // skip the search caused by a programmatic value change
  const blurTimeoutRef = useRef(null)
  const sessionTokenRef = useRef(null)

  // Lazily create a Places session token; one session = N autocompletes + 1 details.
  const ensureSessionToken = () => {
    if (!sessionTokenRef.current) {
      sessionTokenRef.current =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(16).slice(2)}`
    }
    return sessionTokenRef.current
  }

  // Debounced autocomplete whenever the typed value changes (while focused)
  useEffect(() => {
    if (skipSearchRef.current) {
      skipSearchRef.current = false
      return
    }

    if (!focused) return

    const term = (value || '').trim()
    if (term.length < 3) {
      setResults([])
      setLoading(false)
      setOpen(false)
      return
    }

    setLoading(true)
    const handle = setTimeout(async () => {
      const reqId = ++requestIdRef.current
      try {
        const data = await api.autocompleteAddress(tenantId, term, ensureSessionToken())
        if (reqId !== requestIdRef.current) return // stale
        const suggestions = data.suggestions || []
        setResults(suggestions)
        setOpen(suggestions.length > 0)
      } catch (err) {
        if (reqId !== requestIdRef.current) return
        setResults([])
        setOpen(false)
      } finally {
        if (reqId === requestIdRef.current) setLoading(false)
      }
    }, 350)

    return () => clearTimeout(handle)
  }, [value, focused, tenantId])

  // Cleanup the blur timeout on unmount
  useEffect(() => () => clearTimeout(blurTimeoutRef.current), [])

  const handleSelect = async (suggestion) => {
    skipSearchRef.current = true
    setOpen(false)
    setResults([])
    clearTimeout(blurTimeoutRef.current)

    // The session ends on selection — capture the token for Place Details, then reset
    const token = sessionTokenRef.current
    sessionTokenRef.current = null

    // Fill immediately with the description, then upgrade to the full address (with zip)
    onChange(suggestion.description)
    try {
      const data = await api.getPlaceDetails(tenantId, suggestion.placeId, token)
      if (data?.address) {
        skipSearchRef.current = true
        onChange(data.address)
      }
    } catch (err) {
      // Keep the description if the details lookup fails
    }
  }

  const handleFocus = () => {
    clearTimeout(blurTimeoutRef.current)
    setFocused(true)
    if (results.length > 0) setOpen(true)
  }

  const handleBlur = () => {
    // Delay so a result tap registers before the dropdown closes
    blurTimeoutRef.current = setTimeout(() => {
      setFocused(false)
      setOpen(false)
    }, 150)
  }

  return (
    <div className="relative">
      <div className="relative">
        <input
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className="form-input pr-10"
          autoComplete="off"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <MapPin className="w-5 h-5" />
          )}
        </div>
      </div>

      {open && results.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto">
          {results.map((s) => (
            <li key={s.placeId}>
              <button
                type="button"
                // Prevent the input blur from firing before the click registers
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(s)}
                className="w-full text-left px-4 py-2.5 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 flex items-start gap-2"
              >
                <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                <span className="min-w-0">
                  <span className="block text-sm text-gray-900 truncate">{s.mainText}</span>
                  {s.secondaryText && (
                    <span className="block text-xs text-gray-500 truncate">
                      {s.secondaryText}
                    </span>
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
